import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store/hooks';
import { setSelectedPattern, setExperienceLive } from '../../store/slices/exhibitSlice';
import TcButton from '../TcButton';
import TcPaper from '../TcPaper';
import TcImage from '../TcImage';
import LoadingScreen from '../shared/LoadingScreen';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import { useSimpleImagePreloader } from '../../hooks/useImagePreloader';
import TcScanQrCodeBar from '../TcScanQrCodeBar';
import { scrollToTop } from '../../utils/functions';

import imagePeranakan00 from '../../assets/images/exhibit/peranakan/img_peranakan_00.jpg';
import imagePeranakan00temp from '../../assets/images/exhibit/peranakan/img_peranakan_00_temp.jpg';
import imagePeranakan01 from '../../assets/images/exhibit/peranakan/img_peranakan_01.jpg';
import imagePeranakan02 from '../../assets/images/exhibit/peranakan/img_peranakan_02.jpg';
import imagePeranakan03 from '../../assets/images/exhibit/peranakan/img_peranakan_03.jpg';


export default function PeranakanDesign() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [step, setStep] = useState<'intro' | 'patterns' | 'culture'>('intro');
    // const [selectedPattern, setPattern] = useState<string | null>(null);

    // Device detection
    const { orientation } = useDeviceDetection();
    const isLandscape = orientation === 'landscape';
    
    // Preload all images when component mounts
    const allImages = [
        imagePeranakan00,
        imagePeranakan01,
        imagePeranakan02,
        imagePeranakan03
    ];

    const { isLoading, isComplete } = useSimpleImagePreloader(allImages);

    const backToTop = () => {
        setTimeout(() => {
            const rootElement = document.getElementsByClassName('main-content-wrapper')[0] as HTMLElement;
            if (rootElement) scrollToTop(rootElement);
        }, 0);
    };

    const handleSeePatterns = () => {
        setStep('patterns');
        backToTop();
    };

    const handleDiscoverCulture = () => {
        setStep('culture');
        backToTop();
    };

    const handleReturnToIntro = () => {
        setStep('intro');
        backToTop();
    };

    // Show loading state while images are being cached
    if (isLoading) {
        return <LoadingScreen message="Loading images..." />;
    }
    
    // Intro screen
    if (isComplete && step === 'intro') {
        return (
            <div className={`
                main-content-wrapper bg-black 
                ${isLandscape ? '!pt-8' : '!pt-[112px]'} 
                unselectable 
                has-transition 
            `}>
                <TcPaper classNameStyle="mt-4">
                    <h1>{t('exhibits.peranakanDesign.title')}</h1>
                </TcPaper>

                <TcImage classNameStyle="-mt-6 mb-6">
                    <img
                        src={imagePeranakan00}
                        alt={t('exhibits.peranakanDesign.title')}
                        className="w-full"
                    />
                </TcImage>

                <TcPaper classNameStyle="mb-6">
                    <div className="general-content">
                        <h2 
                            className="!text-2xl -mt-24"
                            dangerouslySetInnerHTML={{ __html: t('exhibits.peranakanDesign.subtitle') }} 
                        />
                        <p 
                            dangerouslySetInnerHTML={{ __html: t('exhibits.peranakanDesign.description') }} 
                        />
                    </div>
                </TcPaper>
                
                <div className="button-wrapper flex flex-col gap-4 -mt-6 px-6 !max-w-lg has-transition">
                    <TcButton
                        handleClick={handleSeePatterns}
                        primary
                    >
                        {t('exhibits.peranakanDesign.seePatterns')}
                    </TcButton>
                    <TcButton
                        handleClick={handleDiscoverCulture}
                        primary
                    >
                        {t('exhibits.peranakanDesign.discoverTheCulture')}
                    </TcButton>
                </div>

                <TcScanQrCodeBar classNameStyle="fixed bottom-0 left-0 right-0 z-[20]" />
            </div>
        );
    }
    
    // Pattern selection screen
    if (isComplete && step === 'patterns') {
        return (
            <div className={`
                main-content-wrapper bg-black 
                ${isLandscape ? '!pt-8' : '!pt-[112px]'} 
                unselectable 
                has-transition 
            `}>
                <TcPaper classNameStyle="mt-4">
                    <h1>{t('exhibits.peranakanDesign.patternTitle')}</h1>
                </TcPaper>

                <TcPaper classNameStyle="general-content">
                    <p
                        dangerouslySetInnerHTML={{ __html: t('exhibits.peranakanDesign.patternDescription') }}
                    />
                </TcPaper>

                <TcImage classNameStyle="-mt-4 mb-4">
                    <img
                        src={imagePeranakan00temp}
                        alt={t('exhibits.peranakanDesign.patternTitle')}
                        className="w-full"
                    />
                </TcImage>

                <div className="button-wrapper flex flex-col gap-4 px-6 !max-w-lg has-transition">
                    <TcButton
                        handleClick={handleDiscoverCulture}
                        primary
                    >
                        {t('exhibits.peranakanDesign.discoverTheCulture')}
                    </TcButton>
                    <TcButton
                        handleClick={handleReturnToIntro}
                        primary
                    >
                        {t('exhibits.peranakanDesign.returnToIntro')}
                    </TcButton>
                </div>

                <TcScanQrCodeBar classNameStyle="fixed bottom-0 left-0 right-0 z-[20]" />
            </div>
        );
    }
    
    // Culture screen
    return (
        <div className={`
            main-content-wrapper bg-black 
            ${isLandscape ? '!pt-8' : '!pt-[112px]'} 
            unselectable 
            has-transition 
        `}>
            <TcPaper classNameStyle="mt-4">
                <h1>{t('exhibits.peranakanDesign.cultureTitle')}</h1>
            </TcPaper>

            <TcImage classNameStyle="mb-4">
                <img
                    src={imagePeranakan01}
                    alt={t('exhibits.peranakanDesign.cultureTitle')}
                    className="w-full"
                />
            </TcImage>

            <TcPaper classNameStyle="general-content">
                <p className="mb-4"
                    dangerouslySetInnerHTML={{ __html: t('exhibits.peranakanDesign.cultureContent1') }} 
                />
                <TcImage classNameStyle="mb-4">
                    <img
                        src={imagePeranakan02}
                        alt={t('exhibits.peranakanDesign.cultureContent2')}
                        className="w-full"
                    />
                </TcImage>
                <p className="mb-4"
                    dangerouslySetInnerHTML={{ __html: t('exhibits.peranakanDesign.cultureContent2') }} 
                />
                <TcImage classNameStyle="mb-4">
                    <img
                        src={imagePeranakan03}
                        alt={t('exhibits.peranakanDesign.cultureContent3')}
                        className="w-full"
                    />
                </TcImage>
                <p className="mb-4"
                    dangerouslySetInnerHTML={{ __html: t('exhibits.peranakanDesign.cultureContent3') }} 
                />
            </TcPaper>

            <div className="button-wrapper flex flex-col gap-4 px-6 pb-6 !max-w-lg has-transition">
                <TcButton
                    handleClick={handleSeePatterns}
                    primary
                >
                    {t('exhibits.peranakanDesign.returnToInteractiveTiles')}
                </TcButton>
                <TcButton
                    handleClick={handleReturnToIntro}
                    primary
                >
                    {t('exhibits.peranakanDesign.returnToIntro')}
                </TcButton>
            </div>

            <TcScanQrCodeBar classNameStyle="fixed bottom-0 left-0 right-0 z-[20]" />
        </div>
    );
} 